import BuildShowStore from "~/stores/BuildShowStore";
import Database from "~/lib/Database";
import { Build, Job, CommandJob, RetryableJob, Step } from "~/components/build/Show/lib/types";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce, throttle, dropRight, filter, isNumber, matchesProperty, noop } from "lodash";
import { v4 as uuid } from "uuid";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import useSyncStepsToStore from "~/components/build/Show/lib/useSyncStepsToStore";
import useSyncJobsToStore from "~/components/build/Show/lib/useSyncJobsToStore";
import cable from "~/lib/cable";
import {
  BuildChannelEvent,
  BuildChannelMessage,
} from "~/components/build/Show/lib/types/BuildChannelEvent";
import {
  BuildFetchMeta,
  buildQueryKey,
  buildQueryOptions,
} from "~/components/build/Show/lib/queries/build";
import { annotationsChangedHandler } from "~/components/build/Show/lib/annotationsChangedHandler";

const BuildContext = createContext<NullableBuildContext>({
  build: null,
  setBuildData: noop,
  reloadBuild: noop,
});

export const LegacyBuildContextProvider = ({
  children,
  store,
}: PropsWithChildren<{ store: BuildShowStore }>) => {
  const [build, setBuild] = useState(store.build);
  const setSteps = useBuildStore((state) => state.setSteps);
  const setJobs = useBuildStore((state) => state.setJobs);

  const handleStoreChange = useCallback(() => {
    setBuild(store.build);

    // Sync steps and jobs to the Zustand store synchronously so that
    // components reading from the store (e.g., useRetriesForJob) see
    // updated data in the same render cycle.
    setSteps(store.build.steps, store.build);
    setJobs(store.build.jobs);
  }, [store, setSteps, setJobs]);

  useEffect(() => {
    store.on("change", handleStoreChange);
    return () => {
      store.off("change", handleStoreChange);
    };
  }, []);

  const setBuildData = useCallback(
    (build: Build) => store.loadAndEmit(Database.parse(build)),
    [store],
  );
  const reloadBuild = useCallback(() => store.reload({}), [store]);

  const contextValue = useMemo(
    () => ({ build, setBuildData, reloadBuild }),
    [build, setBuildData, reloadBuild],
  );

  return <BuildContext.Provider value={contextValue}>{children}</BuildContext.Provider>;
};

// Events that must always trigger an immediate refetch (cancel in-flight to get fresh data).
const IMMEDIATE_EVENTS = new Set<BuildChannelEvent>([
  BuildChannelEvent.Started,
  BuildChannelEvent.Failing,
  BuildChannelEvent.Finished,
  BuildChannelEvent.Skipped,
  BuildChannelEvent.Canceling,
  BuildChannelEvent.PipelineChanged,
  BuildChannelEvent.CommitChanged,
]);

export const BuildContextProvider = ({
  children,
  initialBuild,
}: PropsWithChildren<{ initialBuild: Build }>) => {
  const queryClient = useQueryClient();
  const showRetriedJobs = useBuildPreferencesStore((state) => state.showRetriedJobs);

  // Track fetch metadata for server-side telemetry (source, trigger, delay).
  // Mirrors the legacy BuildShowStore behaviour of passing these as query params.
  const sourceId = useRef(uuid());
  const fetchTriggerRef = useRef<string>("initial");
  // Records when the oldest unprocessed event arrived, so we can measure how
  // long throttle/debounce delayed the actual fetch (same as oldestReloadRequestTime
  // in the legacy BuildShowStore).
  const oldestEventTimeRef = useRef<number | null>(null);

  const getFetchMeta = useCallback((): BuildFetchMeta => {
    const trigger = fetchTriggerRef.current;
    const delay =
      oldestEventTimeRef.current != null
        ? Math.round(performance.now() - oldestEventTimeRef.current)
        : 0;

    // Reset after reading so automatic refetches (e.g. window focus) don't
    // replay the last event's trigger/delay values.
    fetchTriggerRef.current = "refetch";
    oldestEventTimeRef.current = null;

    return { source: sourceId.current, trigger, delay };
  }, []);

  // placeholderData ensures build is always defined on first render
  const buildQuery = useQuery(
    buildQueryOptions(
      initialBuild.id,
      initialBuild.jsonPath,
      { placeholderData: initialBuild },
      getFetchMeta,
    ),
  );
  const build = buildQuery.data ?? initialBuild;

  // Extract refetch so throttle/debounce memos and the subscription effect
  // depend only on a stable function reference, not the full query result object.
  const refetchBuild = buildQuery.refetch;

  // Refetch the build immediately, cancelling any in-flight request to ensure fresh data.
  // Used for high-priority events (started, finished, pipelineChanged) where we never
  // want stale data from a request initiated before the event occurred.
  const refetchBuildImmediate = useCallback(
    (event: BuildChannelEvent) => {
      fetchTriggerRef.current = `event:${event}`;
      queryClient.cancelQueries({ queryKey: buildQueryKey(initialBuild.id), exact: true });
      refetchBuild();
    },
    [queryClient, initialBuild.id, refetchBuild],
  );

  // Throttled refetch for best-effort events (stepsChanged, updated, etc.).
  // Fires on the leading edge so the first event in a burst triggers immediately,
  // then at most once per window. Trailing edge ensures a final fetch after events stop.
  const refetchBuildThrottled = useMemo(
    () =>
      throttle(
        (event: BuildChannelEvent) => {
          fetchTriggerRef.current = `event:${event}`;
          refetchBuild();
        },
        2000,
        { leading: true, trailing: true },
      ),
    [refetchBuild],
  );

  // Debounce step/job invalidation to batch rapid ActionCable events
  // (e.g. many jobs finishing in quick succession), matching the old behavior.
  // TODO: Replace with AsyncBatcher for targeted invalidation using step_uuids.
  const debouncedInvalidateStepsAndJobs = useMemo(
    () =>
      debounce(
        () => {
          queryClient.invalidateQueries({
            queryKey: ["build", initialBuild.id, "steps"],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: ["build", initialBuild.id, "jobs"],
            exact: false,
          });
        },
        1000,
        { maxWait: 4000 },
      ),
    [queryClient, initialBuild.id],
  );

  // Debounced invalidation for legacy mode (when ThrottledBuildRefetch is off).
  // Invalidates build, steps, and jobs queries on every event, batched via debounce.
  const debouncedInvalidateAll = useMemo(
    () =>
      debounce(
        () => {
          queryClient.invalidateQueries({
            queryKey: buildQueryKey(initialBuild.id),
            exact: true,
          });
          queryClient.invalidateQueries({
            queryKey: ["build", initialBuild.id, "steps"],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: ["build", initialBuild.id, "jobs"],
            exact: false,
          });
        },
        1000,
        { maxWait: 4000 },
      ),
    [queryClient, initialBuild.id],
  );

  // Subscribe to ActionCable and refetch the build on relevant events.
  useEffect(() => {
    const subscription = cable.subscriptions.create(
      { channel: "Pipelines::BuildChannel", uuid: initialBuild.id },
      {
        received(eventData: BuildChannelMessage) {
          if (!Object.values(BuildChannelEvent).includes(eventData.event as BuildChannelEvent)) {
            return;
          }

          // Record when the first event in a batch arrived so getFetchMeta
          // can compute how long throttle/debounce delayed the actual fetch.
          if (oldestEventTimeRef.current == null) {
            oldestEventTimeRef.current = performance.now();
          }

          // Annotation changes only affect annotation queries and build stats
          // (annotation counts), not steps or jobs — skip steps/jobs invalidation.
          if (
            Features.BuildPageAnnotationsNavigation &&
            eventData.event === BuildChannelEvent.AnnotationsChanged
          ) {
            refetchBuildThrottled(eventData.event);
            annotationsChangedHandler(
              queryClient,
              initialBuild.id,
              eventData.scope,
              eventData.job_uuid,
            );
            return;
          }

          if (Features.ThrottledBuildRefetch) {
            if (IMMEDIATE_EVENTS.has(eventData.event)) {
              // Cancel the throttle timer to prevent a redundant trailing-edge refetch.
              // refetchBuildImmediate also aborts any in-flight HTTP request so the
              // immediate refetch doesn't race with a stale response.
              refetchBuildThrottled.cancel();
              refetchBuildImmediate(eventData.event);
            } else {
              refetchBuildThrottled(eventData.event);
            }

            debouncedInvalidateStepsAndJobs();
          } else {
            debouncedInvalidateAll();
          }
        },
      },
    );

    return () => {
      subscription.unsubscribe();
      refetchBuildThrottled.cancel();
      debouncedInvalidateStepsAndJobs.cancel();
      debouncedInvalidateAll.cancel();
    };
  }, [
    initialBuild.id,
    refetchBuildImmediate,
    refetchBuildThrottled,
    debouncedInvalidateStepsAndJobs,
    debouncedInvalidateAll,
  ]);

  // Sync React Query data into Zustand store
  useSyncStepsToStore(queryClient, build);
  useSyncJobsToStore(queryClient, build, showRetriedJobs);

  const setBuildData = useCallback(
    (newBuild: Build) => {
      queryClient.setQueryData(buildQueryKey(newBuild.id), Database.parse(newBuild));
    },
    [queryClient],
  );

  const reloadBuild = useCallback(() => {
    fetchTriggerRef.current = "manual";
    refetchBuild();
  }, [refetchBuild]);

  const contextValue = useMemo(
    () => ({ build, setBuildData, reloadBuild }),
    [build, setBuildData, reloadBuild],
  );

  return <BuildContext.Provider value={contextValue}>{children}</BuildContext.Provider>;
};

type StrictBuildContext = {
  build: Build;
  setBuildData: (build: Build) => void;
  reloadBuild: () => void;
};

type NullableBuildContext = {
  build: Build | null;
  setBuildData: (build: Build) => void;
  reloadBuild: () => void;
};

// Overload signatures
export function useBuild(): StrictBuildContext;
export function useBuild(options: { strict: false }): NullableBuildContext;

// Implementation
export function useBuild(options?: {
  strict?: boolean;
}): StrictBuildContext | NullableBuildContext {
  const context = useContext(BuildContext);

  if (context === undefined) {
    throw new Error("useBuild() must be used within a <BuildContextProvider />");
  }

  if (options?.strict !== false && !context.build) {
    throw new Error("Build context is missing required build");
  }

  return context;
}

/**
 * Fetch the most recent parallel jobs for a given step.
 */
export const useMostRecentParallelJobsForStep = (stepUuid: Step["uuid"]): CommandJob[] => {
  const jobs = useJobsForStep<CommandJob>(stepUuid);

  return mostRecentParallelJobs(jobs);
};

/**
 * Fetch the most recent matrix jobs for a given step.
 */
export const useMostRecentMatrixJobsForStep = (stepUuid: Step["uuid"]): CommandJob[] => {
  const jobs = useJobsForStep<CommandJob>(stepUuid);

  return mostRecentMatrixJobs(jobs);
};

export function useJobsForStep<T extends Job>(stepUuid: Step["uuid"]): T[] {
  const stepJobs = useBuildStore((state) => state.stepJobs);

  return (stepJobs.get(stepUuid) || []) as T[];
}

export function useMostRecentJob<T extends Job>(stepUuid: Step["uuid"]): T | null {
  const jobs = useJobsForStep<T>(stepUuid);
  if (!jobs.length) {
    return null;
  }
  return jobs[jobs.length - 1];
}

export function findJobsForStep<T extends Job>(build: Build, stepUuid: Step["uuid"]): T[] {
  return (useBuildStore.getState().stepJobs.get(stepUuid) || []) as T[];
}

export function mostRecentParallelJobs(jobs: CommandJob[]): CommandJob[] {
  return Array.from(
    jobs
      .filter((job) => !job.retriedInJobUuid)
      .reduce((acc, job) => {
        acc.set(job.parallelGroupIndex, job);
        return acc;
      }, new Map())
      .values(),
  );
}

export function mostRecentMatrixJobs(jobs: CommandJob[]): CommandJob[] {
  return Array.from(
    jobs
      .filter((job) => !job.retriedInJobUuid)
      .reduce((acc, job) => {
        if (!job.matrix) {
          return acc;
        }

        acc.set(JSON.stringify(job.matrix), job);
        return acc;
      }, new Map())
      .values(),
  );
}

type UseRetriesForJobOptions = { includeLatest?: boolean };

export function useRetriesForJob(
  job: RetryableJob,
  { includeLatest = true }: UseRetriesForJobOptions = {},
): RetryableJob[] {
  let jobs = useJobsForStep<RetryableJob>(job.stepUuid);

  if ("matrix" in job && job.matrix) {
    jobs = filter(jobs, matchesProperty("matrix", job.matrix));
  }

  if ("parallelGroupIndex" in job && isNumber(job.parallelGroupIndex)) {
    jobs = filter(jobs, { parallelGroupIndex: job.parallelGroupIndex });
  }

  if (!includeLatest) {
    return dropRight(jobs, 1);
  }

  return jobs;
}

export default BuildContext;
